import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Alert, Button, Input, InputNumber, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { Form } from '~/components';
import { CheckResponse, sendCSVFile } from '~/api/payrolls';
import styled from 'styled-components';
import theme from '~/theme';
import { useWatch } from 'antd/es/form/Form';
import { ButtonProps } from 'antd/lib/button';
import { FormInstance } from 'antd/lib/form';
import { useCurrentAuthBusiness } from '~/hooks/auth';
import CurrencyInput from '~/components/inputs/CurrencyInput';

const { Dragger } = Upload;

interface Props {
  form: FormInstance;
  goNext: (p: CheckResponse) => void;
}

const EnterPaymentDetails: FC<Props> = (props) => {
  const { goNext, form } = props;
  const [t] = useTranslation('payrolls');
  const sendFile = useMutation(sendCSVFile, {
    onError: () => {},
  });
  const [error, setError] = useState<string | null>(null);
  const business = useCurrentAuthBusiness();
  const currencies = business?.features?.veengu_payroll?.currencies ?? [];

  return (
    <Container>
      <Typography.Title level={4}>{t('Payment details')}</Typography.Title>
      <Form
        form={form}
        name={formName}
        initialValues={currencies.length === 1 ? { [currencyItemName]: currencies[0] } : undefined}
        onValuesChange={() => setError(null)}
        onFinish={(data) => {
          sendFile
            .mutateAsync(data)
            .then((result) => {
              goNext(result.data);
            })
            .catch((error) => setError(error?.response?.data?.message));
        }}
      >
        <FormContent currencies={currencies}>
          {error && <Alert showIcon type="error" message={error} />}
        </FormContent>
        <ContinueButton loading={sendFile.isLoading} />
      </Form>
    </Container>
  );
};

interface FormContentProps {
  currencies: string[];
}

const FormContent: FC<FormContentProps> = (props) => {
  const { currencies } = props;
  const [t] = useTranslation('payrolls');
  const file = useWatch(fileItemName);
  const hasFiles = file?.fileList?.length > 0;

  return (
    <>
      <Input.Group className="DetailsInput-Container">
        <div className="DetailsInput-Item">
          <Typography.Text strong>{t('Number of records')}</Typography.Text>
          <Form.Item
            name={numberOfRecordsItemName}
            noStyle
            rules={[{ required: true, type: 'number' }]}
          >
            <InputNumber className="DetailsInput-Input" placeholder={t('Enter value')} />
          </Form.Item>
        </div>
        <div className="DetailsInput-Item">
          <Typography.Text strong>{t('Total amount to pay')}</Typography.Text>
          <Form.Item
            name={totalAmountItemName}
            noStyle
            rules={[{ required: true, type: 'number' }]}
          >
            <InputNumber className="DetailsInput-Input" placeholder={t('Enter amount')} />
          </Form.Item>
        </div>
        <div className="DetailsInput-Item">
          <Typography.Text strong>{t('Currency')}</Typography.Text>
          <Form.Item
            name={currencyItemName}
            noStyle
            rules={[{ required: true }]}
          >
            <CurrencyInput
              className="DetailsInput-Input"
              currencyFilter={(c: string) => currencies.includes(c)}
            />
          </Form.Item>
        </div>
      </Input.Group>

      <div className="FileInput-Container">
        <Typography.Text className="FileInput-Title">
          {t('Please upload the filled CSV file to proceed')}
        </Typography.Text>
        <Form.Item name={fileItemName}>
          <Dragger {...draggerProps} style={{ display: hasFiles ? 'none' : undefined }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('Click or drag file to this area to upload')}</p>
            <p className="ant-upload-hint">
              {t(
                'Support for a single upload. Strictly prohibited from uploading company data or other banned files.'
              )}
            </p>
          </Dragger>
        </Form.Item>
        {props.children}
      </div>
    </>
  );
};

const ContinueButton: FC<ButtonProps> = (props) => {
  const [tc] = useTranslation();
  const numberOfRecords = useWatch(numberOfRecordsItemName);
  const totalAmount = useWatch(totalAmountItemName);
  const currency = useWatch(currencyItemName);
  const file = useWatch(fileItemName);

  return numberOfRecords && totalAmount && currency && file?.fileList?.length > 0 ? (
    <Button htmlType="submit" form={formName} type="primary" className="SendButton" {...props}>
      {tc('continue')}
    </Button>
  ) : null;
};

const formName = 'paymentDetails';
const numberOfRecordsItemName = 'numberOfRecords';
const totalAmountItemName = 'amount';
const fileItemName = 'file';
const currencyItemName = 'currency';

const draggerProps = {
  name: 'file',
  multiple: false,
  accept: '.csv',
  beforeUpload: () => false,
};

const Container = styled.div`
  .DetailsInput-Container {
    background-color: #fafafa;
    border: 1px solid #e6e6e6;
    border-radius: ${theme.borderRadiusBase}px;
    padding: 12px 16px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    display: flex;
    justify-content: space-between;

    .DetailsInput-Item {
      width: 30%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .DetailsInput-Input {
      width: 100%;
    }
  }

  .FileInput-Container {
    background-color: #fff;
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 12px;
    padding-bottom: 1px;
    display: flex;
    flex-direction: column;

    .FileInput-Title {
      margin-bottom: 8px;
    }

    .ant-upload-list-item-card-actions-btn {
      opacity: 1;
    }
  }

  .ant-upload-hint {
    padding: 0 16px;
  }

  .ant-upload-list-item-name {
    word-break: break-all;
  }

  .ant-alert-error {
    margin-bottom: 8px;
  }
`;

export default EnterPaymentDetails;
